/**
 * DD-BE-PR2b-FILES: design-file + proof API for D&D overlay zones.
 * Gated by products_design.queue.view.
 */
import type { AxiosRequestConfig } from 'axios'
import apiService from '@/services/api'
import {
  narrowPiqQuoteStatus,
  narrowPiqUnitPriceSource,
  type PiqQuoteStatus,
  type PiqUnitPriceSource,
} from '@/lib/dd-queue/types/subjectMutation'

const STRICT_SUCCESS_HTTP: AxiosRequestConfig = {
  validateStatus: (status) => status >= 200 && status < 300,
}

export type DesignDevelopmentQueueFileKind = 'design_file' | 'proof' | 'quote'

export type DesignDevelopmentQueueFileApi = {
  uuid: string
  line_key: string
  kind: DesignDevelopmentQueueFileKind
  category: string | null
  proof_version: number | null
  parent_uuid: string | null
  sort_order: number | null
  packet_snapshot: Record<string, unknown> | null
  original_name: string
  mime: string | null
  size: number
  uploaded_by_name: string | null
  source?: string | null
  uploaded_at: string | null
  download_url: string
}

interface ListEnvelope {
  success: boolean
  data: DesignDevelopmentQueueFileApi[]
}

interface OneEnvelope {
  success: boolean
  data: DesignDevelopmentQueueFileApi
  piq_parse?: {
    outcome?: string | null
    ok?: boolean | null
    message?: string | null
    quote_number?: string | null
    unit_price_cents?: number | null
    piq_quote_status?: string | null
    piq_unit_price_source?: string | null
  } | null
}

export type DesignDevelopmentQueueFileUploadResult = DesignDevelopmentQueueFileApi & {
  piqParse?: {
    outcome?: string | null
    ok?: boolean | null
    message?: string | null
    quoteNumber?: string | null
    unitPriceCents?: number | null
    piqQuoteStatus?: PiqQuoteStatus | null
    piqUnitPriceSource?: PiqUnitPriceSource | null
  } | null
}

function filesBase(submissionUuid: string): string {
  return `/v1/design-development/submissions/${submissionUuid}/files`
}

export async function listDesignDevelopmentQueueFiles(
  submissionUuid: string,
  lineKey?: string
): Promise<DesignDevelopmentQueueFileApi[]> {
  const response = await apiService.get<ListEnvelope>(filesBase(submissionUuid), {
    params: lineKey ? { line_key: lineKey } : undefined,
    ...STRICT_SUCCESS_HTTP,
  })
  if (!response?.success || !Array.isArray(response.data)) {
    throw new Error('Failed to load design-development files')
  }
  return response.data
}

export async function uploadDesignDevelopmentQueueFile(args: {
  submissionUuid: string
  file: File
  lineKey: string
  kind: DesignDevelopmentQueueFileKind
  category?: string
  parentUuid?: string
  onProgress?: (pct: number) => void
}): Promise<DesignDevelopmentQueueFileUploadResult> {
  const body = new FormData()
  body.append('file', args.file)
  body.append('line_key', args.lineKey)
  body.append('kind', args.kind)
  if (args.kind === 'design_file' && args.category) {
    body.append('category', args.category)
  }
  if (args.parentUuid) {
    body.append('parent_uuid', args.parentUuid)
  }

  const response = await apiService.post<OneEnvelope>(filesBase(args.submissionUuid), body, {
    ...STRICT_SUCCESS_HTTP,
    onUploadProgress: (event) => {
      if (!args.onProgress || !event.total) return
      args.onProgress(Math.round((event.loaded / event.total) * 100))
    },
  })
  if (!response?.success || !response.data) {
    throw new Error('Failed to upload file')
  }
  const piq = response.piq_parse
  return {
    ...response.data,
    piqParse: piq
      ? {
          outcome: piq.outcome ?? null,
          ok: piq.ok ?? null,
          message: piq.message ?? null,
          quoteNumber: piq.quote_number ?? null,
          unitPriceCents: piq.unit_price_cents ?? null,
          piqQuoteStatus: narrowPiqQuoteStatus(piq.piq_quote_status),
          piqUnitPriceSource: narrowPiqUnitPriceSource(piq.piq_unit_price_source),
        }
      : null,
  }
}

export async function reorderDesignDevelopmentSupportingFiles(args: {
  submissionUuid: string
  parentUuid: string
  orderedUuids: string[]
}): Promise<DesignDevelopmentQueueFileApi[]> {
  const response = await apiService.patch<ListEnvelope>(
    `${filesBase(args.submissionUuid)}/${args.parentUuid}/supporting-order`,
    { ordered_uuids: args.orderedUuids },
    STRICT_SUCCESS_HTTP
  )
  if (!response?.success || !Array.isArray(response.data)) {
    throw new Error('Failed to reorder supporting files')
  }
  return response.data
}

export async function deleteDesignDevelopmentQueueFile(
  submissionUuid: string,
  fileUuid: string
): Promise<void> {
  await apiService.delete(`${filesBase(submissionUuid)}/${fileUuid}`, STRICT_SUCCESS_HTTP)
}

/** Auth-bearing download — bare <a href> to download_url omits Bearer and gets 401. */
export async function downloadDesignDevelopmentQueueFileBlob(
  submissionUuid: string,
  fileUuid: string
): Promise<Blob> {
  const response = await apiService.getWithResponse<Blob>(
    `${filesBase(submissionUuid)}/${fileUuid}/download`,
    {
      responseType: 'blob',
      ...STRICT_SUCCESS_HTTP,
    }
  )
  return response.data
}

export function openBlobInNewTab(blob: Blob): string {
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener,noreferrer')
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
  return url
}

